import PageHero from "@/components/PageHero";
import { FadeUp } from "@/components/Motion";
import SeoHead from "@/components/SeoHead";

/**
 * The nine research parameters.
 *
 * Presented as a numbered sequence because the framework is cumulative — each
 * parameter narrows the view established by the one before it, and the closing
 * statement depends on the reader having followed that order.
 */
const research = [
  {
    title: "Market Research & Analysis",
    body: "We examine the broader property market to understand current conditions, emerging trends and the forces influencing demand and supply. Our research considers different property sectors, locations and market segments to identify opportunities, challenges and changing market dynamics.",
  },
  {
    title: "Economic Indicators",
    body: "Property markets are closely linked to the wider economy. We monitor key indicators such as interest rates, inflation, employment, GDP growth, business activity and credit conditions to understand how the economic environment may influence property prices, rents, financing costs and investment sentiment.",
  },
  {
    title: "Government Policies",
    body: "Government policies can significantly influence property demand, affordability, supply and investment behaviour. We assess relevant regulations, cooling measures, taxation, housing policies, development initiatives and other policy changes to understand their potential impact on specific properties and the wider market.",
  },
  {
    title: "Pricing & Transaction Trends",
    body: "Understanding what properties are actually transacting for is fundamental to sound property analysis. We examine historical and current transaction prices, price movements, rental trends, comparable properties and transaction volumes to determine whether an asset appears fairly valued, relatively attractive or potentially overpriced.",
  },
  {
    title: "Investment Performance",
    body: "We assess the investment characteristics and potential performance of a property over its intended holding period. This includes considerations such as rental yield, capital appreciation potential, income generation, holding costs and overall return expectations, allowing clients to evaluate the property beyond its initial purchase price.",
  },
  {
    title: "Financial Feasibility",
    body: "Every investment needs to make financial sense. We analyse the complete financial equation, including acquisition costs, financing, interest expenses, rental income, taxes, CPF considerations, cash flow and potential exit proceeds. Where appropriate, different scenarios are modelled to understand how changing assumptions may affect the investment outcome.",
  },
  {
    title: "Risk Assessment",
    body: "No property investment is without risk. We identify and evaluate factors that could affect an investment's performance, including market volatility, financing exposure, liquidity, tenure, regulatory changes, tenant or rental risks, oversupply and potential changes in demand. Understanding the downside is an essential part of making a sound investment decision.",
  },
  {
    title: "Portfolio Optimisation",
    body: "A property should not be assessed in isolation when it forms part of a wider portfolio. We examine the relationship between existing and potential assets, considering diversification, concentration, liquidity, income generation, capital growth and risk exposure. Our objective is to determine whether an investment strengthens the overall portfolio or creates unnecessary exposure.",
  },
  {
    title: "Long-Term Wealth Objectives",
    body: "Ultimately, property decisions should support the client's broader financial and life objectives. We consider factors such as investment horizon, income requirements, capital preservation, wealth accumulation, retirement planning, succession considerations and future liquidity needs. This ensures that property recommendations remain aligned with the client's long-term goals rather than being driven solely by short-term market opportunities.",
  },
];

/** The eight values. Each carries a summary line, an explanation and a principle. */
const values = [
  {
    k: "Integrity",
    lead: "Advice we would give our own family.",
    d: "We act with honesty, consistency and professional judgement, always putting the client's interests first. We present opportunities, risks and limitations openly, even when the answer may be difficult.",
    principle: "Do what is right.",
  },
  {
    k: "Independence",
    lead: "Advice first. Transaction second.",
    d: "Our advice is guided by research, evidence and the client's objectives — not by sales pressure. We are prepared to recommend proceeding, waiting or walking away when appropriate.",
    principle: "The right decision comes first.",
  },
  {
    k: "Research",
    lead: "Evidence before opinion.",
    d: "We combine market intelligence, transaction data, financial analysis, policy and risk assessment to form a well-rounded view. We distinguish facts, assumptions and professional judgement.",
    principle: "Research deeply. Advise thoughtfully.",
  },
  {
    k: "Transparency",
    lead: "Clear assumptions. Clear reasoning.",
    d: "We make the evidence, assumptions, risks and trade-offs behind our recommendations visible and understandable.",
    principle: "Nothing important is hidden.",
  },
  {
    k: "Client Commitment",
    lead: "Your objectives. Your timeline.",
    d: "We tailor our advice to each client's circumstances, priorities and long-term goals — without pressure to transact.",
    principle: "Your interests come first.",
  },
  {
    k: "Professional Excellence",
    lead: "High standards. Every engagement.",
    d: "We apply discipline, rigour and care across our research, analysis and client relationships, continually improving how we advise.",
    principle: "Excellence is our standard.",
  },
  {
    k: "Long-Term Thinking",
    lead: "Beyond the transaction.",
    d: "We consider how today's property decision may affect your portfolio, cash flow, wealth and objectives over the years ahead.",
    principle: "Think beyond today.",
  },
  {
    k: "Collective Expertise",
    lead: "Three perspectives. One considered view.",
    d: "Our advisors combine complementary experience, challenge assumptions and collaborate through a shared research and advisory methodology.",
    principle: "Better perspectives. Better decisions.",
  },
];

const standard = [
  "Integrity guides our conduct.",
  "Independence protects our judgement.",
  "Research strengthens our conclusions.",
  "Transparency builds trust.",
  "Client commitment keeps the focus where it belongs.",
  "Professional excellence sets our standard.",
  "Long-term thinking shapes our advice.",
  "Collective expertise broadens our perspective.",
];

export default function OurCompany() {
  return (
    <>
      <SeoHead pageKey="our_company" />
      <PageHero
        eyebrow="About PIRAC"
        chapter="Chapter — Our Company"
        lines={["Your Trusted Partner in", "Singapore Real Estate."]}
        blurb="Independent property advice. Research-driven analysis. Long-term strategy."
        image="/assets/hero-our-company"
        imageAlt="A laptop showing Singapore market data beside a PIRAC notebook, with the Marina Bay skyline through the window, next to a panel reading research, insight, strategy and informed decisions"
      />

      <section className="bg-[#060f1e] py-24 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-5">
            <FadeUp><span className="overline">Mandate</span></FadeUp>
            <FadeUp delay={0.1}>
              <h2 className="mt-6 font-serif text-4xl md:text-5xl lg:text-6xl text-[#f7f5f0] font-light tracking-tight leading-[1.05]">
                Property, seen clearly.
              </h2>
            </FadeUp>

            {/* The column under this heading was empty down the whole length of
                the copy beside it, which is the gap the client pointed at. */}
            <FadeUp delay={0.2}>
              <a
                href="/assets/company-advisory-approach.jpeg"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-12 block border border-[#1a2940] hover:border-[#cca24d]/50 transition-colors"
                aria-label="PIRAC's eight-step advisory approach — open full size"
              >
                <img
                  src="/assets/company-advisory-approach-1536.webp"
                  srcSet="/assets/company-advisory-approach-800.webp 800w, /assets/company-advisory-approach-1536.webp 1536w"
                  sizes="(min-width: 1024px) 40vw, 92vw"
                  alt="PIRAC's advisory approach in eight steps: define your investment goals, research the market, choose your investment type, financial planning and budgeting, risk management, due diligence and property evaluation, renovation and value addition, and legal and regulatory awareness"
                  className="w-full h-auto block"
                  width="1432"
                  height="1098"
                  loading="lazy"
                />
              </a>
            </FadeUp>
            <a
              href="/assets/company-advisory-approach.jpeg"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 min-h-11 py-2 inline-flex items-center gap-2 text-sm text-[#9ca3af] hover:text-[#cca24d] transition-colors"
            >
              <span className="h-px w-6 bg-[#cca24d]" />
              Open the full-size diagram
            </a>
          </div>
          <div className="col-span-12 lg:col-span-6 lg:col-start-7 text-[#d8d8d8] leading-relaxed space-y-6">
            <FadeUp>
              <p>
                PIRAC provides independent property advice grounded in rigorous research, market
                intelligence and strategic analysis. We help clients navigate Singapore's real estate
                landscape with greater clarity — identifying opportunities, understanding risks and
                making decisions with confidence.
              </p>
            </FadeUp>
            <FadeUp delay={0.1}>
              <p>
                We advise across residential, commercial, industrial and mixed-use property, bringing
                together market research, financial analysis and investment strategy to develop a clear
                perspective on every opportunity.
              </p>
            </FadeUp>
            <FadeUp delay={0.15}>
              <p>
                Every engagement begins with the client's objectives, circumstances and aspirations. Our
                advice is personalised to the individual — never based on a standard template or a
                one-size-fits-all approach.
              </p>
            </FadeUp>
            <FadeUp delay={0.2}>
              <p>
                Our focus is long-term value. We believe a property decision should be evaluated not
                simply by the transaction it creates today, but by the position it leaves the client in
                for the years and decade that follow.
              </p>
            </FadeUp>
            <FadeUp delay={0.25}>
              <p>
                That means looking beyond price and transaction to consider value, risk, cash flow,
                market cycles, portfolio positioning and exit strategy.
              </p>
            </FadeUp>
            <FadeUp delay={0.3}>
              <p className="text-[#f7f5f0]">
                At PIRAC, our role is not simply to help clients buy or sell property.
                <br />
                It is to help them make better property decisions.
              </p>
            </FadeUp>
            <FadeUp delay={0.35}>
              <div className="pt-4 overline text-[#cca24d]/80">
                Research. Insight. Strategy. Informed Decisions.
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ----------------------------------------------- Research methodology */}
      <section className="bg-[#0c1728] border-y border-[#1a2940] py-24 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <FadeUp><span className="overline">Research Methodology</span></FadeUp>
          <FadeUp delay={0.1}>
            <h2 className="mt-6 font-serif text-4xl md:text-5xl lg:text-6xl text-[#f7f5f0] font-light tracking-tight max-w-3xl leading-[1.05]">
              The Framework <em className="text-[#cca24d] not-italic font-normal italic">Behind Our Advice.</em>
            </h2>
          </FadeUp>
          <FadeUp delay={0.15}>
            <h3 className="mt-10 font-serif text-2xl md:text-3xl text-[#f7f5f0] font-light">
              How We Assess Every Opportunity
            </h3>
          </FadeUp>
          <FadeUp delay={0.2}>
            <p className="mt-5 mb-14 max-w-3xl text-lg text-[#d8d8d8] font-light leading-relaxed">
              At PIRAC, every property or investment opportunity is assessed through a structured
              research framework. We examine market conditions, economic factors, policy developments,
              financial performance and risk — while keeping the client's long-term objectives at the
              centre of our analysis.
            </p>
          </FadeUp>

          <FadeUp>
            <a
              href="/assets/company-research-framework.jpeg"
              target="_blank"
              rel="noopener noreferrer"
              className="block border border-[#1a2940] hover:border-[#cca24d]/50 transition-colors"
              aria-label="The nine research criteria behind PIRAC's advice — open full size"
            >
              <img
                src="/assets/company-research-framework-1536.webp"
                srcSet="/assets/company-research-framework-800.webp 800w, /assets/company-research-framework-1536.webp 1536w"
                sizes="(min-width: 1024px) 94vw, 92vw"
                alt="PIRAC's nine research criteria arranged around a circle: market research and analysis, economic indicators, government policies, pricing and transaction trends, investment performance, financial feasibility, risk assessment, portfolio optimisation, and long-term wealth objectives"
                className="w-full h-auto block"
                width="1536"
                height="1024"
                loading="lazy"
              />
            </a>
            <a
              href="/assets/company-research-framework.jpeg"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 min-h-11 py-2 inline-flex items-center gap-2 text-sm text-[#9ca3af] hover:text-[#cca24d] transition-colors"
            >
              <span className="h-px w-6 bg-[#cca24d]" />
              Open the full-size diagram
            </a>
          </FadeUp>

          <details className="mt-6 group/d">
            <summary className="cursor-pointer list-none min-h-11 py-2 inline-flex items-center gap-2 text-sm text-[#9ca3af] hover:text-[#cca24d] transition-colors">
              <span className="h-px w-6 bg-[#cca24d]" />
              Read the nine criteria as text
            </summary>
            <dl className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#1a2940]">
              {research.map((r, i) => (
                <div key={r.title} className="bg-[#0c1728] p-8">
                  <div className="text-xs text-[#cca24d]/60 tnum mb-4">{String(i + 1).padStart(2, "0")}</div>
                  <dt className="font-serif text-xl text-[#f7f5f0] leading-tight mb-4">{r.title}</dt>
                  <dd className="text-sm text-[#9ca3af] leading-relaxed">{r.body}</dd>
                </div>
              ))}
            </dl>
          </details>

          <FadeUp delay={0.1}>
            <div className="mt-px bg-[#060f1e] border-t border-[#cca24d]/25 p-8 lg:p-14">
              <h3 className="font-serif text-3xl md:text-4xl text-[#f7f5f0] font-light tracking-tight">
                One Integrated <em className="text-[#cca24d] not-italic font-normal italic">Research Framework.</em>
              </h3>
              <div className="mt-6 space-y-5 max-w-4xl text-[#d8d8d8] leading-relaxed">
                <p>
                  These nine parameters are designed to work together. No single indicator can provide a
                  complete picture of a property opportunity.
                </p>
                <p>
                  By combining market intelligence, economic analysis, policy research, transaction data,
                  financial modelling, risk assessment, portfolio analysis and long-term objectives, PIRAC
                  develops a more comprehensive and objective view of each opportunity.
                </p>
              </div>
              <div className="mt-10 pt-8 border-t border-[#1a2940] overline text-[#cca24d]/80">
                Research first. Analyse deeply. Advise with clarity.
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      <section className="bg-[#060f1e] py-24 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-1 md:grid-cols-2 gap-8">
          <FadeUp>
            <div className="p-10 lg:p-14 border border-[#1a2940] h-full">
              <span className="overline">Mission</span>
              <p className="mt-8 font-serif text-3xl md:text-4xl text-[#f7f5f0] font-light leading-tight">
                To deliver independent, research-driven property advice that enables clients to make confident and informed decisions.
              </p>
            </div>
          </FadeUp>
          <FadeUp delay={0.1}>
            <div className="p-10 lg:p-14 border border-[#cca24d]/40 bg-[#0c1728] h-full">
              <span className="overline">Vision</span>
              <p className="mt-8 font-serif text-3xl md:text-4xl text-[#f7f5f0] font-light leading-tight">
                To become one of Singapore's most trusted property research and advisory companies.
              </p>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ------------------------------------------------------------ Values */}
      <section className="bg-[#060f1e] py-24 lg:py-32 border-t border-[#1a2940]">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <FadeUp><span className="overline">Our Values</span></FadeUp>
          <FadeUp delay={0.1}>
            <h2 className="mt-6 font-serif text-4xl md:text-5xl lg:text-6xl text-[#f7f5f0] font-light tracking-tight max-w-3xl leading-[1.05]">
              Eight standards that <em className="text-[#cca24d] not-italic font-normal italic">guide every engagement.</em>
            </h2>
          </FadeUp>
          <FadeUp delay={0.15} className="mt-10">
            <a
              href="/assets/company-values.jpeg"
              target="_blank"
              rel="noopener noreferrer"
              className="block border border-[#1a2940] hover:border-[#cca24d]/50 transition-colors"
              aria-label="The eight standards that guide every PIRAC engagement — open full size"
            >
              <img
                src="/assets/company-values-1536.webp"
                srcSet="/assets/company-values-800.webp 800w, /assets/company-values-1536.webp 1536w"
                sizes="(min-width: 1024px) 94vw, 92vw"
                alt="PIRAC's eight values: integrity, independence, research, transparency, client commitment, professional excellence, long-term thinking and collective expertise — each with its guiding principle"
                className="w-full h-auto block"
                width="1536"
                height="1024"
                loading="lazy"
              />
            </a>
            <a
              href="/assets/company-values.jpeg"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 min-h-11 py-2 inline-flex items-center gap-2 text-sm text-[#9ca3af] hover:text-[#cca24d] transition-colors"
            >
              <span className="h-px w-6 bg-[#cca24d]" />
              Open the full-size diagram
            </a>
          </FadeUp>

          <details className="mt-6">
            <summary className="cursor-pointer list-none min-h-11 py-2 inline-flex items-center gap-2 text-sm text-[#9ca3af] hover:text-[#cca24d] transition-colors">
              <span className="h-px w-6 bg-[#cca24d]" />
              Read the eight values as text
            </summary>
            <dl className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-px bg-[#1a2940]">
              {values.map((v, i) => (
                <div key={v.k} className="bg-[#0c1728] p-8 lg:p-10">
                  <div className="text-xs text-[#cca24d] tnum mb-5">{String(i + 1).padStart(2, "0")}</div>
                  <dt className="font-serif text-2xl text-[#f7f5f0]">{v.k}</dt>
                  <dd>
                    <p className="mt-2 text-sm text-[#cca24d]/80 italic">{v.lead}</p>
                    <p className="mt-4 text-sm text-[#9ca3af] leading-relaxed">{v.d}</p>
                    <p className="mt-5 pt-5 border-t border-[#1a2940] text-sm text-[#d8d8d8]">
                      <span className="text-[#9ca3af]">Our principle:</span> {v.principle}
                    </p>
                  </dd>
                </div>
              ))}
            </dl>
          </details>

          <FadeUp delay={0.1}>
            <div className="mt-px bg-[#0c1728] border-t border-[#cca24d]/25 p-8 lg:p-14">
              <h3 className="font-serif text-3xl md:text-4xl text-[#f7f5f0] font-light tracking-tight">
                The PIRAC <em className="text-[#cca24d] not-italic font-normal italic">Standard.</em>
              </h3>
              <p className="mt-6 max-w-3xl text-[#d8d8d8] leading-relaxed">
                These eight values define how we work and the standards we bring to every engagement.
              </p>
              <ul className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-3">
                {standard.map((line) => (
                  <li key={line} className="flex items-start gap-3 text-sm text-[#d8d8d8]">
                    <span className="mt-2 h-px w-4 bg-[#cca24d] shrink-0" />
                    {line}
                  </li>
                ))}
              </ul>
              <p className="mt-10 pt-8 border-t border-[#1a2940] max-w-4xl text-[#d8d8d8] leading-relaxed">
                Together, they form the PIRAC Standard — a commitment to providing property advice that
                is thoughtful, independent, research-led and aligned with the client's long-term
                interests.
              </p>
            </div>
          </FadeUp>
        </div>
      </section>
    </>
  );
}
